import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { EditableInput } from "@/utilities/Components";
import {
  dialCodes,
  isValidEmail,
  isValidValue,
  SwitchButton,
  tokens,
} from "@/utilities";
import { useEffect, useMemo, useState } from "react";
import { CareerProps, DefaultValue, FinancialSupport as FinancialSupportType, InstituteProps, facultyProps } from "@/types";
import careersService from "@/api/careers.service";
import Swal from "sweetalert2";
import {
  hasFinancialSupportData,
  resolveCareerFinancialSupport,
  resolveFacultyFinancialSupport,
  resolveInstituteFinancialSupport,
} from "./formaters.hook";

interface Props {
  premiumPlan: string;
  isEditable: boolean;
  fieldsToEdit: string;
  updateCareer: (careerMap: Partial<CareerProps>, id: string) => void;
  selectedCareer: CareerProps;
  institute?: InstituteProps | null;
  faculty?: facultyProps | null;
}

const buildInitialFinancialState = (
  career: CareerProps,
  inst?: InstituteProps | null,
  fac?: facultyProps | null
) => {
  const defaultFinancial = (career.financialSupport?.default || "ies") as DefaultValue;

  const resolvedIes = resolveInstituteFinancialSupport(inst, career.financialSupport?.ies);
  const resolvedFaculty = resolveFacultyFinancialSupport(fac, resolvedIes, career.financialSupport?.faculty);
  const resolvedCareer = resolveCareerFinancialSupport(career);

  return {
    defaultFinancial,
    financialSupport: {
      default: defaultFinancial,
      ies: resolvedIes,
      faculty: resolvedFaculty,
      career: resolvedCareer,
    },
  };
};

export const FinancialSupport = ({
  premiumPlan,
  isEditable,
  fieldsToEdit,
  selectedCareer,
  updateCareer,
  institute,
  faculty,
}: Props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [isEditingFinancialSupport, setIsEditingFinancialSupport] =
    useState<boolean>(false);

  const initialState = useMemo(
    () => buildInitialFinancialState(selectedCareer, institute, faculty),
    [selectedCareer, institute, faculty]
  );

  const [defaultFinancialValue, setDefaultFinancialValue] =
    useState<DefaultValue>(initialState.defaultFinancial);

  const [countryFinancialSupp, setCountryFinancialSupp] = useState<string>(
    initialState.financialSupport[initialState.defaultFinancial]?.phone_code_country || "+57"
  );

  const [editableFinancialSupport, setEditableFinancialSupport] = useState<
    Partial<CareerProps>
  >({
    financialSupport: initialState.financialSupport,
  });

  const isFinancialSupportActive = Boolean(
    editableFinancialSupport.financialSupport?.[defaultFinancialValue]?.hasFinancialSupport
  );

  useEffect(() => {
    if (!isEditingFinancialSupport) {
      const state = buildInitialFinancialState(selectedCareer, institute, faculty);
      setDefaultFinancialValue(state.defaultFinancial);
      setEditableFinancialSupport({
        financialSupport: state.financialSupport,
      });
      const pref = state.financialSupport[state.defaultFinancial]?.phone_code_country;
      setCountryFinancialSupp(pref && pref !== "Prefijo" ? pref : "+57");
    }
  }, [selectedCareer, institute, faculty, isEditingFinancialSupport]);

  async function handleFinancialSupportSubmit() {
    try {
      if (selectedCareer.id) {
        const currentCareer = (editableFinancialSupport.financialSupport?.career || {}) as FinancialSupportType;
        const currentIes = editableFinancialSupport.financialSupport?.ies;
        const currentFaculty = editableFinancialSupport.financialSupport?.faculty;

        const careerHasData = hasFinancialSupportData(currentCareer);
        const shouldCareerHaveFinSupport =
          currentCareer.hasFinancialSupport === false
            ? false
            : (currentCareer.hasFinancialSupport ?? (defaultFinancialValue === "career" || careerHasData));

        const updatedCareer: FinancialSupportType = {
          ...currentCareer,
          hasFinancialSupport: Boolean(shouldCareerHaveFinSupport),
        };

        const updatedIes: FinancialSupportType | undefined = currentIes
          ? {
              ...currentIes,
              hasFinancialSupport: hasFinancialSupportData(currentIes) || Boolean(currentIes.hasFinancialSupport),
            }
          : undefined;

        const updatedFaculty: FinancialSupportType | undefined = currentFaculty
          ? {
              ...currentFaculty,
              hasFinancialSupport: hasFinancialSupportData(currentFaculty) || Boolean(currentFaculty.hasFinancialSupport),
            }
          : undefined;

        let activeHasFinancialSupport = false;
        if (defaultFinancialValue === "career") {
          activeHasFinancialSupport = Boolean(updatedCareer.hasFinancialSupport);
        } else if (defaultFinancialValue === "faculty") {
          activeHasFinancialSupport = Boolean(updatedFaculty?.hasFinancialSupport);
        } else {
          activeHasFinancialSupport = Boolean(updatedIes?.hasFinancialSupport);
        }

        const data: Partial<CareerProps> = {
          ...editableFinancialSupport,
          hasFinancialSupport: activeHasFinancialSupport,
          financialSupport: {
            ...editableFinancialSupport.financialSupport,
            default: defaultFinancialValue,
            ...(updatedIes ? { ies: updatedIes } : {}),
            ...(updatedFaculty ? { faculty: updatedFaculty } : {}),
            career: updatedCareer,
          },
        };

        console.warn("Updating financial support in Database ...");
        await careersService.update(
          selectedCareer.uidInstitution,
          selectedCareer.uidFaculty,
          selectedCareer.id,
          data
        );
        updateCareer(data, selectedCareer.id);
        console.warn("Updated financial support in Database!");
        Swal.fire({
          title: "Los cambios se han guardado exitosamente",
          icon: "success",
          timer: 2000,
        });
      }
      setIsEditingFinancialSupport(false);
    } catch (error) {
      console.error("Error updating financial support information", error);
    }
  }

  const handleChangeFinancialSupp = (
    event: React.MouseEvent<HTMLElement>,
    financialDefault: DefaultValue
  ) => {
    if (financialDefault != null) {
      event.preventDefault();
      setDefaultFinancialValue(financialDefault);
      const newCountry =
        editableFinancialSupport.financialSupport?.[financialDefault]?.phone_code_country;
      setCountryFinancialSupp(newCountry && newCountry !== "Prefijo" ? newCountry : "+57");
      setEditableFinancialSupport((prevInfo) => {
        const currentCareer = (prevInfo.financialSupport?.career || {}) as FinancialSupportType;
        const updatedCareer = {
          ...currentCareer,
          ...(financialDefault === "career"
            ? {
                hasFinancialSupport:
                  typeof currentCareer.hasFinancialSupport === "boolean"
                    ? currentCareer.hasFinancialSupport
                    : true,
              }
            : {}),
        };

        return {
          ...prevInfo,
          financialSupport: {
            ...prevInfo.financialSupport,
            default: financialDefault,
            career: updatedCareer,
          },
        };
      });
    }
  };

  const handleFinancialSupportChange = (
    event: React.ChangeEvent<any> | SelectChangeEvent<any>
  ) => {
    const { name, value } = event.target;
    switch (name) {
      case "phone_code_country":
        setCountryFinancialSupp(value);
        break;
      default:
        break;
    }
    setEditableFinancialSupport((prevInfo) => {
      const currentCareer = (prevInfo.financialSupport?.career || {}) as FinancialSupportType;
      const updatedCareer: FinancialSupportType = {
        ...currentCareer,
        [name]: value,
      };

      if (updatedCareer.hasFinancialSupport !== false && hasFinancialSupportData(updatedCareer)) {
        updatedCareer.hasFinancialSupport = true;
      }

      return {
        ...prevInfo,
        financialSupport: {
          ...prevInfo.financialSupport,
          default: prevInfo.financialSupport?.default || defaultFinancialValue || "ies",
          career: updatedCareer,
        },
      };
    });
  };

  const handleFinancialSupportSwitchChange = (
    switchName: "hasWhatsapp" | "hasFinancialSupport",
    value?: boolean
  ) => {
    setEditableFinancialSupport((prevValues) => {
      const currentCareer = (prevValues.financialSupport?.career || {}) as FinancialSupportType;
      const newValue = typeof value === "boolean" ? value : !Boolean(currentCareer[switchName]);
      const updatedCareer: FinancialSupportType = {
        ...currentCareer,
        [switchName]: newValue,
      };
      if (switchName === "hasWhatsapp" && updatedCareer.hasWhatsapp) {
        updatedCareer.hasFinancialSupport = true;
      }
      return {
        ...prevValues,
        financialSupport: {
          ...prevValues.financialSupport,
          default: prevValues.financialSupport?.default || defaultFinancialValue || "ies",
          career: updatedCareer,
        },
      };
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexWrap: "wrap",
        position: "relative",
      }}
    >
      {isEditingFinancialSupport ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              right: { xs: 0, sm: 0 },
              top: { xs: "35px", sm: "40px" },
              marginBottom: { xs: "20px", sm: "0" },
              display: "flex",
            }}
            onClick={handleFinancialSupportSubmit}
          >
            Guardar
          </Button>
          {!isEditable && (
            <Typography variant="h5" sx={{ margin: "0", color: "red" }}>
              {premiumPlan}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              right: { xs: 0, sm: 0 },
              top: { xs: "35px", sm: "40px" },
              marginBottom: { xs: "20px", sm: "0" },
              display: "flex",
            }}
            onClick={() =>
              setIsEditingFinancialSupport(!isEditingFinancialSupport)
            }
          >
            Editar
          </Button>
        </Box>
      )}
      <Typography
        sx={{
          padding: "20px 0",
          color: colors.grey[100],
          marginTop: "10px",
        }}
        variant="h3"
      >
        Apoyo Financiero
      </Typography>

      <hr style={{ width: "100%", marginBottom: "25px" }} />

      {isEditingFinancialSupport && (
        <Box sx={{ marginBottom: "20px", width: "100%" }}>
          <ToggleButtonGroup
            color="primary"
            value={defaultFinancialValue}
            exclusive
            onChange={handleChangeFinancialSupp}
            aria-label="Platform"
          >
            <ToggleButton value="ies">
              Reusa datos de la Institución
            </ToggleButton>
            <ToggleButton value="faculty">
              Reusa datos de la Facultad
            </ToggleButton>
            <ToggleButton value="career">Usa datos personalizados</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box
        sx={{
          marginBottom: "20px",
          display: "flex",
          width: "100%",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold" }}
          color={colors.grey[100]}
        >
          Tiene Apoyo Financiero
        </Typography>
        {isEditingFinancialSupport &&
        defaultFinancialValue === fieldsToEdit ? (
          <SwitchButton
            checked={
              editableFinancialSupport.financialSupport?.career
                ?.hasFinancialSupport ?? false
            }
            setChecked={(checked) =>
              handleFinancialSupportSwitchChange("hasFinancialSupport", checked)
            }
            isDisabled={!isEditable}
          />
        ) : (
          <Typography variant="h5" color={colors.grey[500]}>
            {editableFinancialSupport.financialSupport?.[
              defaultFinancialValue
            ]?.hasFinancialSupport
              ? "Si"
              : "No"}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          marginBottom: "20px",
          display: "flex",
          width: "100%",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold" }}
          color={colors.grey[100]}
        >
          Web/Enlace/Brochure
        </Typography>
        <EditableInput
          key={`website_${defaultFinancialValue}`}
          value={
            editableFinancialSupport.financialSupport?.[defaultFinancialValue]
              ?.website ?? ""
          }
          name="website"
          type="text"
          isEditing={
            isEditingFinancialSupport && defaultFinancialValue === fieldsToEdit
          }
          isDisabled={!isEditable || !isFinancialSupportActive}
          onChange={handleFinancialSupportChange}
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: { xs: "flex", sm: "grid" },
          flexDirection: "column",
          gridTemplateColumns: {
            sm: "repeat(1,minmax(0,100%))",
            md: "repeat(2,minmax(0,100%))",
          },
          gap: "20px",
        }}
      >
        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Email/Contacto
          </Typography>
          <EditableInput
            key={`email_${defaultFinancialValue}`}
            value={
              editableFinancialSupport.financialSupport?.[defaultFinancialValue]
                ?.email ?? ""
            }
            name="email"
            type="text"
            error={
              Boolean(
                editableFinancialSupport.financialSupport?.[defaultFinancialValue]?.email &&
                !isValidEmail(editableFinancialSupport.financialSupport[defaultFinancialValue]!.email!)
              )
            }
            helperText={
              Boolean(
                editableFinancialSupport.financialSupport?.[defaultFinancialValue]?.email &&
                !isValidEmail(editableFinancialSupport.financialSupport[defaultFinancialValue]!.email!)
              )
                ? "El correo electrónico debe tener la extensión .edu"
                : ""
            }
            isEditing={
              isEditingFinancialSupport &&
              defaultFinancialValue === fieldsToEdit
            }
            isDisabled={!isEditable || !isFinancialSupportActive}
            onChange={handleFinancialSupportChange}
          />
        </Box>

        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Teléfono de contacto
          </Typography>
          {isEditingFinancialSupport &&
          defaultFinancialValue === fieldsToEdit ? (
            <FormControl
              id="phone"
              variant="outlined"
              fullWidth
              size="small"
              sx={{ display: "flex", flexDirection: "row" }}
            >
              <Select
                value={countryFinancialSupp && countryFinancialSupp !== "Prefijo" ? countryFinancialSupp : "+57"}
                id="phone_code_country"
                label="Prefijo"
                name="phone_code_country"
                onChange={handleFinancialSupportChange}
                disabled={!isEditable || !isFinancialSupportActive}
                sx={{
                  display: "flex",
                  border: "none",
                  width: { xs: "40%", sm: "45%" },
                  color: colors.grey[100],
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      width: "40ch",
                      right: 0,
                      margin: "0 0 0 24px",
                      color: colors.grey[100],
                    },
                  },
                }}
              >
                {dialCodes.map((country) => (
                  <MenuItem key={country.code} value={country.dial_code}>
                    ({country.dial_code}) {country.name}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                id="contactPhone"
                name="contactPhone"
                type="text"
                value={
                  editableFinancialSupport.financialSupport?.[
                    defaultFinancialValue
                  ]?.contactPhone ?? ""
                }
                fullWidth
                autoComplete="off"
                variant="outlined"
                onChange={handleFinancialSupportChange}
                disabled={!isEditable || !isFinancialSupportActive}
                error={
                  !isValidValue(
                    "phone",
                    editableFinancialSupport.financialSupport?.[
                      defaultFinancialValue
                    ]?.contactPhone
                  )
                }
                helperText={
                  !isValidValue(
                    "phone",
                    editableFinancialSupport.financialSupport?.[
                      defaultFinancialValue
                    ]?.contactPhone
                  )
                    ? "Sólo se permiten números"
                    : ""
                }
                InputLabelProps={{
                  style: { color: colors.grey[100], fontSize: "16px" },
                }}
                InputProps={{
                  style: {
                    color: colors.grey[100],
                    fontSize: "16px",
                    width: "100%",
                  },
                }}
              />
            </FormControl>
          ) : (
            <Typography variant="h5" color={colors.grey[200]}>
              {(() => {
                const phone =
                  editableFinancialSupport.financialSupport?.[defaultFinancialValue]?.contactPhone;
                const rawPref =
                  editableFinancialSupport.financialSupport?.[defaultFinancialValue]?.phone_code_country;
                const pref = rawPref && rawPref !== "Prefijo" ? rawPref : "+57";
                if (!phone) return "";
                return `(${pref}) ${phone}`;
              })()}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography
        sx={{
          padding: "20px 0",
          color: colors.grey[100],
          marginTop: "10px",
        }}
        variant="h4"
      >
        Redes Sociales
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: { xs: "flex", sm: "grid" },
          flexDirection: "column",
          gridTemplateColumns: {
            sm: "repeat(1,minmax(0,100%))",
            md: "repeat(2,minmax(0,100%))",
          },
          gap: "20px",
        }}
      >
        {/*-----Whatsapp-----*/}
        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Whatsapp
          </Typography>
          <EditableInput
            key={`whatsapp_${defaultFinancialValue}`}
            value={
              editableFinancialSupport.financialSupport?.[defaultFinancialValue]
                ?.whatsapp ?? ""
            }
            name="whatsapp"
            type="text"
            error={
              !isValidValue(
                "phone",
                editableFinancialSupport.financialSupport?.[
                  defaultFinancialValue
                ]?.whatsapp
              ) &&
              (editableFinancialSupport.financialSupport?.[
                defaultFinancialValue
              ]?.hasWhatsapp ??
                false)
            }
            helperText={
              !isValidValue(
                "phone",
                editableFinancialSupport.financialSupport?.[
                  defaultFinancialValue
                ]?.whatsapp
              ) &&
              editableFinancialSupport.financialSupport?.[defaultFinancialValue]
                ?.hasWhatsapp
                ? "Sólo se permiten números y debe incluir el código del pais. Ej. 573115552244"
                : ""
            }
            isEditing={
              isEditingFinancialSupport &&
              defaultFinancialValue === fieldsToEdit
            }
            isDisabled={
              !isEditable ||
              !isFinancialSupportActive ||
              !editableFinancialSupport.financialSupport?.[
                defaultFinancialValue
              ]?.hasWhatsapp
            }
            onChange={handleFinancialSupportChange}
          />
        </Box>

        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Whatsapp Activo
          </Typography>
          {isEditingFinancialSupport &&
          defaultFinancialValue === fieldsToEdit ? (
            <SwitchButton
              checked={
                editableFinancialSupport.financialSupport?.career
                  ?.hasWhatsapp ?? false
              }
              setChecked={(checked) =>
                handleFinancialSupportSwitchChange("hasWhatsapp", checked)
              }
              isDisabled={!isEditable || !isFinancialSupportActive}
            />
          ) : (
            <Typography variant="h5" color={colors.grey[500]}>
              {editableFinancialSupport.financialSupport?.[
                defaultFinancialValue
              ]?.hasWhatsapp
                ? "Si"
                : "No"}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
